import { ConsoleLogger } from 'aws-amplify/utils';
import React, {JSX} from "react";
import {Alert, Badge, Card, Col, OverlayTrigger, Popover, Row} from "react-bootstrap";
import {ApiCallStatus} from "../../CommonFunc/Enum";
import {ColumnMetadata} from "./ColumnMetadata";
import "../../CommonCss/TableToggle.css"
import "../../CommonCss/ExtraColor.css"
import "../../CommonCss/TableFilterMultiSelect.css"
import {
    ColumnDef,
} from "@tanstack/react-table";
import {ROW_LIMIT} from "./RowLimit";
import {Command} from "react-bootstrap-icons";
import {TableCellFormater} from "../../CommonFunc/TableCellFormater";
import {TableColumnFormatter} from "../../CommonFunc/TableColumnFormatter";
import {TanStackTableView} from "./TanStackViewTable";

const logger = new ConsoleLogger("SearchResultTableViewOnly.tsx")

const MULTI_SELECT_COLUMN = [
    {
        table: "vw_depmap_all_omics_with_in_house_cellline_25q2",
        column: ["gene", "cellline"]
    },
    {
        table: "vw_depmap_cellline_models_with_in_house_cellline_25q2",
        column: ["oncotreelineage"]
    }
]

interface SearchResultTableViewOnlyProps {
    title: string
    smallTitle?: string
    legends: any
    rowClasses: ((row: any, rowIndex: number) => string)
    refreshToken: string
    tableName: string

    dataAdmin: string

    columnMetadata: ColumnMetadata[]
    columnMetadataStatus: ApiCallStatus
    columnMetadataErrorMessage: string

    tableData: any[]
    tableDataStatus: ApiCallStatus
    tableDataErrorMessage: string

    tableDefinitionStatus: ApiCallStatus
    tableDefinitionErrorMessage: string
    tableDescription: string

    displayPagination: boolean
    enableColumnFilter: boolean
    enableSorting: boolean
}

interface SearchResultTableViewOnlyState {
    overallApiCallStatus: ApiCallStatus
}

// Custom filter definition
interface TableColumnFilterProps {
    type: 'text' | 'multiSelect'
    options?: string[]
    placeholder?: string
    className?: string
}

export class SearchResultTableViewOnly extends React.Component<
    SearchResultTableViewOnlyProps,
    SearchResultTableViewOnlyState
> {
    constructor(props: SearchResultTableViewOnlyProps) {
        super(props)
        this.state = {
            overallApiCallStatus: ApiCallStatus.NoData,
        }
    }

    setApiOverallStatus() {
        // If one return error, overall is error
        if (this.props.columnMetadataStatus === ApiCallStatus.Error ||
            this.props.tableDefinitionStatus === ApiCallStatus.Error ||
            this.props.tableDataStatus === ApiCallStatus.Error) {
            return this.setState({
                overallApiCallStatus: ApiCallStatus.Error
            })
        }

        // If none return error, and at least one still loading, overall is loading
        if (this.props.columnMetadataStatus === ApiCallStatus.Loading ||
            this.props.tableDefinitionStatus === ApiCallStatus.Loading ||
            this.props.tableDataStatus === ApiCallStatus.Loading) {
            return this.setState({
                overallApiCallStatus: ApiCallStatus.Loading
            })
        }

        if (this.props.columnMetadataStatus === ApiCallStatus.Completed &&
            this.props.tableDefinitionStatus === ApiCallStatus.Completed &&
            this.props.tableDataStatus === ApiCallStatus.Completed) {
            return this.setState({
                overallApiCallStatus: ApiCallStatus.Completed
            })
        }

        return this.setState({
            overallApiCallStatus: ApiCallStatus.NoData
        })
    }

    columnHeaderFormatter(column: ColumnMetadata): JSX.Element {
        // eslint-disable-next-line no-template-curly-in-string
        const popoverId = "${this.props.tableName}-${column.name}-definition"
        const isMultiSelectColumn = this.isMultiSelect(column.name)

        return (
            <OverlayTrigger
                placement={"top"}
                delay={{show: 250, hide: 400}}
                overlay={
                    <Popover className={"ms-2"}
                             id={popoverId}>
                        <Popover.Header as={"h3"}>Definition</Popover.Header>
                        <Popover.Body>
                            {column.definition || column.displayName}
                        </Popover.Body>
                    </Popover>
                }
            >
                <div style={{ minWidth: '10rem' }}>
                    {isMultiSelectColumn && (
                        <div className={"mb-1"}>
                            Hold <Badge bg={"dark"} text={"white"} style={{fontSize: "0.75rem"}}><Command/></Badge> or <Badge bg={"dark"} text={"white"} style={{fontSize: "0.75rem"}}>Ctrl</Badge> for multi-select
                        </div>
                    )}
                    <div className={"d-flex align-items-center"}>
                        <label htmlFor={popoverId}>{column.displayName.trim()}</label>
                    </div>
                </div>
            </OverlayTrigger>
        )
    }

    createFilterComponent(filterProps: TableColumnFilterProps): (props: { column: any }) => JSX.Element {
        return ({ column }: { column: any }) => {
            if (filterProps.type === 'multiSelect' && filterProps.options) {
                return (
                    <div className="multi-select-container">
                        <select
                            multiple
                            value={column.getFilterValue() || []}
                            onChange={e => {
                                const selected = Array.from(e.target.selectedOptions).map(o => o.value)
                                column.setFilterValue(selected.length ? selected : undefined)
                            }}
                            className={`form-select form-select-sm ${filterProps.className || ''}`}
                            style={{
                                minHeight: '80px',
                                maxHeight: '150px',
                                width: '100%',
                                border: '1px solid #ced4da'
                            }}
                        >
                            <option value='All'>All</option>
                            {filterProps.options.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                )
            } else {
                return (
                    <div>
                        <input
                            type='text'
                            value={column.getFilterValue() || ''}
                            onChange={e => column.setFilterValue(e.target.value)}
                            placeholder={filterProps.placeholder || 'Filter column'}
                            className={`form-control form-control-sm ${filterProps.className || ''}`}
                            style={{ width: '100%' }}
                        />
                    </div>
                )
            }
        }
    }

    isMultiSelect(column: string): boolean {
        for (let tableObj of MULTI_SELECT_COLUMN) {
            if (tableObj.table === this.props.tableName && tableObj.column.includes(column)) {
                return true
            }
        }
        return false
    }

    constructFilter(column: string): TableColumnFilterProps {
        if (this.isMultiSelect(column)) {
            // Extract multi-select dropdown
            const uniqueColumnValues: string[] = Array.from(
                new Set(this.props.tableData.map(row => row[column]).filter(Boolean))
            ).sort()
            return {
                type: 'multiSelect',
                options: uniqueColumnValues,
                placeholder: 'All',
                className: 'table-multi-select-filter'
            }
        }

        return {
            type: 'text',
            placeholder: 'Filter column',
            className: TableColumnFormatter.renderCssClassForFilter(this.props.columnMetadata.length)
        }
    }

    setColumnDescription(): ColumnDef<any, any>[] {
        const {columnMetadata} = this.props
        return columnMetadata
            .filter((col) => col.defaultDisplay !== "remove") // Skip columns marked as "remove"
            .map((col) => {
                const columnName = col.name
                const isHidden = col.defaultDisplay === "hide"
                const filterProps = this.constructFilter(columnName)

                const filterFn = filterProps.type === 'multiSelect'
                    ? (row: any, columnId: string, value: string[]) => {
                        if (!value || value.length === 0 || value.includes('All')) {
                            return true
                        }
                        return value.includes(row.getValue(columnId))
                    } : 'includesString'

                return {
                    id: columnName,
                    accessorFn: (row: any) => row[columnName],
                    header: () => this.columnHeaderFormatter(col),
                    cell: (info: any) => {
                        const value = info.getValue()
                        const row = info.row.original
                        return TableCellFormater.cellFormatter(value, row, info.row.index, info.column.id) ?? null
                    },
                    enableSorting: true,
                    enableColumnFilter: this.props.enableColumnFilter,
                    filterFn: filterFn,
                    meta: {
                        hidden: isHidden,
                        displayName: col.displayName.trim(),
                        filterComponent: this.createFilterComponent(filterProps)
                    }
                }
            })
    }

    componentDidMount() {
        this.setApiOverallStatus()
    }

    componentDidUpdate(prevProps: Readonly<SearchResultTableViewOnlyProps>) {
        if (prevProps.refreshToken !== this.props.refreshToken ||
            prevProps.columnMetadataStatus !== this.props.columnMetadataStatus ||
            prevProps.tableDefinitionStatus !== this.props.tableDefinitionStatus ||
            prevProps.tableDataStatus !== this.props.tableDataStatus ||
            prevProps.tableData !== this.props.tableData) {
            this.setApiOverallStatus()
        }
    }

    render() {
        const {
            title,
            smallTitle,
            tableName,
            tableData,
            tableDescription,
            tableDataErrorMessage,
            columnMetadataErrorMessage,
            tableDefinitionErrorMessage,
        } = this.props

        const { overallApiCallStatus } = this.state
        const columns = this.setColumnDescription()

        logger.debug("render - tableData = ", this.props.tableData)

        return (
            <>
                {title && <h3 className={"text-primary mt-3"}>{title}</h3>}
                {smallTitle && <p className={"mt-3"}>{smallTitle}</p>}
                <Card className="bg-light mb-3">
                    <Card.Header>
                        <Row>
                            <Col md={12}>
                                <p>{tableDescription}</p>
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body>
                        {overallApiCallStatus === ApiCallStatus.Loading && (
                            <div className={"text-primary"}>
                                Searching ...
                            </div>
                        )}

                        {overallApiCallStatus === ApiCallStatus.Error && (
                            <div className="text-danger">
                                {columnMetadataErrorMessage && <p>{columnMetadataErrorMessage}</p>}
                                {tableDefinitionErrorMessage && <p>{tableDefinitionErrorMessage}</p>}
                                {tableDataErrorMessage && <p>{tableDataErrorMessage}</p>}
                            </div>
                        )}

                        {overallApiCallStatus === ApiCallStatus.Completed && tableData.length >= ROW_LIMIT[tableName] && (
                            <Alert variant={"warning"}>
                                <Alert.Heading>Warning!</Alert.Heading>
                                Too many results. Only display up to {ROW_LIMIT[tableName]} entries.
                            </Alert>
                        )}

                        {overallApiCallStatus === ApiCallStatus.Completed && (
                            <TanStackTableView
                                data={tableData.slice(0, ROW_LIMIT[tableName])}
                                columns={columns}
                                tableName={this.props.tableName}
                            />
                        )}
                    </Card.Body>
                </Card>
            </>
        )
    }
}